import axios from "axios";
import { API_BASE_URL } from "../apiClient";

/**
 * Batch APIs
 * Test-mode friendly version:
 * - Tries correct batch base URL first
 * - Falls back to old direct API_BASE_URL paths
 */

const BASE_URL = API_BASE_URL;
const BATCH_API_BASE_URL = `${BASE_URL}/api/public/batches`;
const SUGG_BASE_URL = `${BASE_URL}/api/public/batches/suggestions`;

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return {
    Authorization: token ? `Bearer ${token}` : "",
    "Content-Type": "application/json",
  };
}

const createApiClient = (baseURL) =>
  axios.create({
    baseURL,
    headers: authHeaders(),
  });


const normalizeArray = (data) => (Array.isArray(data) ? data : []);

const getLivestockForBatch = async (batchId) => {
  if (!batchId) return [];
  try {
    const { data } = await axios.get(`${BASE_URL}/api/public/livestock/batch/${batchId}`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn("Livestock fallback failed for batch", batchId, err?.response?.status);
    return [];
  }
};

const classifyBatchStatus = (batch) => {
  const today = new Date();
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const start = batch?.startDate ? new Date(`${batch.startDate}T00:00:00`) : null;
  const end = batch?.endDate ? new Date(`${batch.endDate}T00:00:00`) : null;

  if (batch?.closed === true) return "Completed";
  if (start && todayOnly < start) return "Upcoming";
  if (end && todayOnly > end) return "Overdue";
  return "Active";
};

const hydrateBasicBatchRows = async (rows) => {
  const batches = normalizeArray(rows);
  return Promise.all(
    batches.map(async (batch) => {
      const livestock = await getLivestockForBatch(batch.id);
      const count = livestock.length;
      const historicalAnimalCosts = livestock.reduce((sum, item) => sum + (Number(item.cost) || 0), 0);
      const legacyPerGoatCost = (Number(batch.purchasePricePerGoat) || 0) * count;
      const batchPurchaseCost = Number(batch.totalPurchasePrice) || 0;
      const investment = batchPurchaseCost > 0 ? batchPurchaseCost
        : (historicalAnimalCosts > 0 ? historicalAnimalCosts : legacyPerGoatCost);
      const soldAnimals = livestock.filter((item) => item.sold || item.currentStatus === "SOLD");
      const recordedSaleCount = soldAnimals.filter((item) => item.saleAmount !== undefined && item.saleAmount !== null && item.saleAmount !== "").length;
      const revenue = soldAnimals.reduce((sum, item) => sum + (Number(item.saleAmount) || 0), 0);
      const financialsFinalized = batch.closed === true && soldAnimals.length === count
        && count > 0 && recordedSaleCount === soldAnimals.length && investment > 0;
      const marketPrice = Number(batch.marketPricePerKg);
      const estimatedLivestockValue = marketPrice > 0
        ? livestock.filter((animal) => !animal.sold && animal.currentStatus !== "SOLD")
          .reduce((sum, animal) => sum + (Number(animal.weight) || 0), 0) * marketPrice
        : null;
      return {
        ...batch,
        status: classifyBatchStatus(batch),
        totalCount: count,
        livestockCount: count,
        investment,
        revenue,
        financialsFinalized,
        finalProfit: financialsFinalized ? revenue - investment : null,
        finalRoiPercentage: financialsFinalized ? ((revenue - investment) / investment) * 100 : null,
        roiPercentage: financialsFinalized ? ((revenue - investment) / investment) * 100 : 0,
        estimatedLivestockValue,
        estimatedUnrealizedPl: estimatedLivestockValue !== null ? revenue + estimatedLivestockValue - investment : null,
        progressPercent: Number(batch.progressPercent ?? 0),
      };
    })
  );
};

const getWithFallback = async (urls, config = {}) => {
  let lastError = null;

  for (const url of urls) {
    try {
      const { data } = await axios.get(url, {
        ...config,
        headers: authHeaders(),
      });

      console.log("Batch API success:", url);
      return data;
    } catch (err) {
      console.warn("Batch API failed:", url, err?.response?.status);
      lastError = err;
    }
  }

  throw lastError;
};

const batchService = {
  /** Phase-5: server KPIs */
  getEnhancedBatches: async () => {
    try {
      return await getWithFallback([
        `${BATCH_API_BASE_URL}/enhanced/all`,
        `${BATCH_API_BASE_URL}/enhanced`,
      ]);
    } catch (enhancedError) {
      console.warn("Enhanced batch API failed. Loading basic rows and hydrating from livestock.", enhancedError?.response?.status);
      const basicRows = await getWithFallback([
        `${BATCH_API_BASE_URL}/all`,
        `${BATCH_API_BASE_URL}`,
      ]);
      return hydrateBasicBatchRows(basicRows);
    }
  },

  /** Phase-5: filtered enhanced list */
  getFilteredBatches: async ({ type, status, from, to } = {}) => {
    const params = {};
    if (type) params.type = type;
    if (status) params.status = status;
    if (from) params.from = from;
    if (to) params.to = to;

    try {
      return await getWithFallback(
        [
          `${BATCH_API_BASE_URL}/enhanced`,
        ],
        { params }
      );
    } catch (enhancedError) {
      console.warn("Filtered enhanced batch API failed. Loading basic rows and hydrating from livestock.", enhancedError?.response?.status);
      const basicRows = await getWithFallback([
        `${BATCH_API_BASE_URL}/all`,
        `${BATCH_API_BASE_URL}`,
      ]);
      return hydrateBasicBatchRows(basicRows);
    }
  },

  /** Minimal list */
  getAllBatches: async () => {
    return getWithFallback([
      `${BATCH_API_BASE_URL}/all`,
      `${BATCH_API_BASE_URL}`,
    ]);
  },

  /** Get one */
  getById: async (id) => {
    return getWithFallback([
      `${BATCH_API_BASE_URL}/${id}`,
      `${API_BASE_URL}/${id}`,
    ]);
  },

  /** Admin CRUD */
  createBatch: async (payload) => {
    const urls = [
      `${BATCH_API_BASE_URL}/admin`,
      `${API_BASE_URL}/admin`,
    ];

    let lastError = null;

    for (const url of urls) {
      try {
        const { data } = await axios.post(url, payload, {
          headers: authHeaders(),
        });

        console.log("Create batch success:", url);
        return data;
      } catch (err) {
        console.warn("Create batch failed:", url, err?.response?.status);
        lastError = err;
      }
    }

    throw lastError;
  },

  updateBatch: async (id, payload) => {
    const urls = [
      `${BATCH_API_BASE_URL}/admin/${id}`,
      `${API_BASE_URL}/admin/${id}`,
    ];

    let lastError = null;

    for (const url of urls) {
      try {
        const { data } = await axios.put(url, payload, {
          headers: authHeaders(),
        });

        console.log("Update batch success:", url);
        return data;
      } catch (err) {
        console.warn("Update batch failed:", url, err?.response?.status);
        lastError = err;
      }
    }

    throw lastError;
  },

  getDeleteImpact: async (id) => {
    const { data } = await axios.get(`${BATCH_API_BASE_URL}/admin/${id}/delete-impact`, {
      headers: authHeaders(),
    });
    return data;
  },

  deleteBatch: async (id, cascade = false) => {
    try {
      await axios.delete(`${BATCH_API_BASE_URL}/admin/${id}`, {
        headers: authHeaders(),
        params: { cascade },
      });
      console.log("Delete batch success:", `${BATCH_API_BASE_URL}/admin/${id}`);
      return;
    } catch (err) {
      console.warn("Delete batch failed:", `${BATCH_API_BASE_URL}/admin/${id}`, err?.response?.status, err?.response?.data);
      throw err;
    }
  },


  deleteBatches: async (ids = []) => {
    if (!Array.isArray(ids) || ids.length === 0) return;

    try {
      await axios.post(`${BATCH_API_BASE_URL}/admin/bulk-delete`, { ids }, { headers: authHeaders() });
      console.log("Bulk delete batches success:", `${BATCH_API_BASE_URL}/admin/bulk-delete`);
      return;
    } catch (err) {
      console.warn("Bulk delete batches failed:", `${BATCH_API_BASE_URL}/admin/bulk-delete`, err?.response?.status, err?.response?.data);
      throw err;
    }
  },

  // -------- Suggestions --------
  suggestSellPrice: async ({ species, goal }) => {
    const client = createApiClient(SUGG_BASE_URL);
    const { data } = await client.get("/sell-price", {
      params: { species, goal },
    });
    return data;
  },

  suggestDurationDays: async ({ species, goal }) => {
    const client = createApiClient(SUGG_BASE_URL);
    const { data } = await client.get("/duration-days", {
      params: { species, goal },
    });
    return data;
  },

  suggestLocation: async ({ species }) => {
    const client = createApiClient(SUGG_BASE_URL);
    const res = await client.get("/location", {
      params: { species },
      validateStatus: () => true,
    });

    if (res.status === 204) return null;
    return res.data;
  },

  suggestLifecycle: async ({ species, goal, start }) => {
    const client = createApiClient(SUGG_BASE_URL);
    const params = { species };

    if (goal) params.goal = goal;
    if (start) params.start = start;

    const { data } = await client.get("/lifecycle", { params });
    return data;
  },
};

export default batchService;
